import { Modal, notification } from "antd";
import React from "react";
import useGetReceiptConfig from "../../apis/tryst/get-receipt-config";
import usePostReceiptConfig from "../../apis/tryst/post-receipt-config";
import PrimaryButton from "../../molecules/button/primary-button";
import useReceiptCustomisationStore from "../../stores/receipt-customisation/receipt-customisation-store";
import { useMyAccessProfileV2 } from "../../apis/tryst/get-my-access-profile";
import { DateAndTimeConfigForReceipt } from "@elixir/types";
import { ReceiptConfigManager } from "../receipt-v2/receipt-config-manager";

const receiptConfigManager = new ReceiptConfigManager();

const SubmitAndResetComponent = ({
  isInsideProfile,
}: {
  isInsideProfile: boolean;
}) => {
  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);

  const {
    clinicId,
    selectedDoctorId,
    margin,
    columns,
    header,
    footer,
    others,
    setReceiptState,
  } = useReceiptCustomisationStore();

  const { data: myAccessProfile } = useMyAccessProfileV2();

  // Use selectedDoctorId from store, fallback to myAccessProfile
  const currentDoctorId =
    selectedDoctorId || myAccessProfile?.loginmeta?.docid!;

  const { mutateAsync, isLoading } = usePostReceiptConfig({
    doctor_oid: currentDoctorId, // Use selected doctor
    clinic_id: clinicId,
  });

  const handleResetClick = () => {
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const { data: resposneData } = useGetReceiptConfig({
    doctor_oid: currentDoctorId, // Use selected doctor
    clinic_id: clinicId,
  });

  const { data: getReceiptConfigData } = resposneData || {};

  const handleResetState = () => {
    const config = getReceiptConfigData?.[0];
    const {
      margins,
      flags,
      header_image_url,
      heights,
      footer_image_url,
      page_size,
      constraints,
    } = config || {};
    const {
      print_paymode_split,
      print_receipt_number,
      print_uhid,
      print_header,
      print_footer,
      discount_column,
      quantity_column,
      date_and_time,
      print_amount_in_words,
      print_transaction_id,
      bill_created_at,
      print_bill_created_by,
    } = flags || {};

    setReceiptState({
      ...(margins ? { margin: margins } : null),
      others: {
        showUHID: !!print_uhid,
        showPaymentMethod: !!print_paymode_split,
        showReceiptNumber: !!print_receipt_number,
        requireUhid: !!constraints?.require_uhid,
        date_and_time: date_and_time || DateAndTimeConfigForReceipt.DATE_ONLY,
        print_amount_in_words: print_amount_in_words ?? false,
        bill_created_at: bill_created_at ?? false,
        print_bill_created_by: print_bill_created_by ?? false,
      },
      header: {
        headerHeight: heights?.header || 2,
        showHeader: !!print_header,
        headerImageSrc: header_image_url,
      },
      footer: {
        footerHeight: heights?.footer || 2,
        showFooter: !!print_footer,
        footerImageSrc: footer_image_url,
        showPaymentId: print_transaction_id,
      },
      columns: {
        discount: discount_column || "ALWAYS",
        quantity: quantity_column || "ALWAYS",
        page_size: page_size || "A4",
      },
    });

    handleClose();
  };

  const handleSaveButtonClick = async () => {
    const { headerImageFile, headerImageSrc, showHeader, headerHeight } =
      header;
    const { footerImageFile, footerImageSrc, showFooter, footerHeight } =
      footer;
    if (!isLoading && currentDoctorId) {
      const { success, error } = await mutateAsync({
        doctor_oid: currentDoctorId, // Use selected doctor
        clinic_id: clinicId,
        margins: margin,
        page_size: columns.page_size,
        heights: {
          header: headerHeight,
          footer: footerHeight,
        },
        flags: {
          discount_column: columns.discount,
          quantity_column: columns.quantity,
          print_header: showHeader,
          print_footer: showFooter,
          print_uhid: others.showUHID,
          print_paymode_split: others.showPaymentMethod,
          print_receipt_number: others.showReceiptNumber,
          print_transaction_id: !!footer.showPaymentId,
          date_and_time:
            others.date_and_time || DateAndTimeConfigForReceipt.DATE_ONLY,
          print_amount_in_words: others.print_amount_in_words || false,
          bill_created_at: others.bill_created_at || false,
          print_bill_created_by: others.print_bill_created_by || false,
        },
        constraints: {
          require_uhid: !!others.requireUhid,
        },
        ...(headerImageSrc ? { header_image_url: headerImageSrc } : null),
        ...(headerImageFile ? { receipt_pdf_header: headerImageFile } : null),
        ...(footerImageSrc ? { footer_image_url: footerImageSrc } : null),
        ...(footerImageFile ? { receipt_pdf_footer: footerImageFile } : null),
      });

      if (success) {
        notification.success({
          message: "New Template Saved!",
          duration: 3,
          placement: "bottomRight",
        });
        receiptConfigManager.invalidateConfigOfClinic(clinicId, currentDoctorId);
        if (!isInsideProfile) {
          history.back();
        }
        setReceiptState({
          hasChanged: false,
        });
        return;
      }
      if (error) {
        notification.error({
          message: "Error in saving template! Please try again.",
          duration: 3,
          placement: "bottomRight",
        });
      }
    }
  };

  return (
    <div className="dr-sticky dr-bottom-0 dr-flex justify-between items-center p-16 bg-darwin-neutral-50 dr-rounded-b-8">
      {/* TODO: React 18 types issue */}
      {/* @ts-ignore */}
      <Modal
        title="Are you sure you want to reset?"
        visible={isModalOpen}
        onOk={handleResetState}
        onCancel={handleClose}
        cancelText="Cancel"
        okText="Yes, Reset"
      >
        <div>All the changes you have made will be lost.</div>
      </Modal>
      <div
        className="text-16 font-700 leading-8 cursor-pointer"
        onClick={handleResetClick}
      >
        Reset
      </div>
      <PrimaryButton
        title="Save Changes"
        className="w-219"
        onClick={handleSaveButtonClick}
        isLoading={isLoading}
      />
    </div>
  );
};

export default SubmitAndResetComponent;
